/**
 * Story 049 — `kpi-defects-this-month` widget body.
 *
 * Reads `GET /api/lyse/v1/defects?createdSince=<first-of-month>&pageSize=1`
 * per story §"Widget set". `total` is the headline number.
 *
 * UX brief §3.3 — single big number, optional sub-caption with the
 * month name, no delta.
 */
import { useMemo, type ReactElement } from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import { typography } from "@aeriosiq/ui";
import { WidgetRetryPill } from "./WidgetRetryPill.js";

interface DefectsResponse {
  total?: number;
  items?: unknown[];
}

function firstOfMonthIso(): string {
  const now = new Date();
  const first = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  return first.toISOString();
}

function monthLabel(): string {
  const fmt = new Intl.DateTimeFormat(undefined, {
    month: "long",
    year: "numeric",
  });
  return fmt.format(new Date());
}

export function KpiDefectsThisMonth(): ReactElement {
  const api = useApi();
  const query = useQuery<DefectsResponse>({
    queryKey: ["dashboard", "kpi", "defects-this-month"],
    queryFn: async () => {
      const response = await api.get<DefectsResponse>("/defects", {
        params: {
          createdSince: firstOfMonthIso(),
          pageSize: 1,
        },
      });
      return response.data;
    },
    staleTime: 60_000,
    retry: false,
  });

  const total = useMemo(() => {
    if (typeof query.data?.total === "number") return query.data.total;
    return 0;
  }, [query.data?.total]);

  if (query.isPending) {
    return (
      <Body>
        <Skeleton variant="rounded" width="50%" height={48} />
        <Skeleton variant="text" width="35%" sx={{ mt: 1 }} />
      </Body>
    );
  }

  if (query.isError) {
    return (
      <Body>
        <WidgetRetryPill
          message="Couldn't load Defects this month."
          onRetry={() => {
            void query.refetch();
          }}
        />
      </Body>
    );
  }

  return (
    <Body>
      <Typography
        component="div"
        sx={{
          fontSize: typography.size["display-md"],
          fontWeight: typography.weight.semibold,
          letterSpacing: typography.tracking["display-md"],
          fontVariantNumeric: "tabular-nums",
          color: "text.primary",
          lineHeight: 1.05,
        }}
      >
        {total}
      </Typography>
      <Typography
        variant="caption"
        component="div"
        sx={{
          mt: 1,
          color: "text.secondary",
          fontSize: typography.size["caption-lg"],
        }}
      >
        {total === 0
          ? "No defects logged this month — clean slate."
          : monthLabel()}
      </Typography>
    </Body>
  );
}

function Body({ children }: { children: React.ReactNode }): ReactElement {
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        px: 2,
      }}
    >
      {children}
    </Box>
  );
}
